import { DollarSign, Sparkles, MessageSquare, CheckCircle } from "lucide-react"

const steps = [
  {
    icon: DollarSign,
    title: "Name Your Price",
    description: "Post what you need and set the price you're willing to pay. You're in control from the start.",
    color: "from-green-500 to-emerald-500",
  },
  {
    icon: Sparkles,
    title: "Receive & Compare Offers",
    description:
      "Verified providers respond with their offers, pricing, and availability. Choose what works best for you.",
    color: "from-[#DB4789] to-pink-500",
  },
  {
    icon: MessageSquare,
    title: "Chat & Book Instantly",
    description: "Negotiate if needed, finalize terms, and confirm your booking. Simple, transparent, and secure.",
    color: "from-blue-500 to-cyan-500",
  },
]

export function HowItWorks() {
  return (
    <section className="py-20 md:py-32 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 left-0 w-72 h-72 bg-[#75055B]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-[#DB4789]/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#DB4789]/10 rounded-full mb-6">
              <span className="text-sm font-semibold text-[#75055B]">Simple Process</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Three simple steps to fair prices and quality service
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12 mb-16">
            {steps.map((step, index) => (
              <div key={index} className="relative group">
                <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-gray-100 hover:border-[#DB4789] transition-all duration-500 hover:shadow-2xl hover:shadow-[#DB4789]/20 h-full">
                  <div className="flex flex-col items-center text-center">
                    {/* Icon with gradient background */}
                    <div className="relative mb-8">
                      <div className={`w-24 h-24 rounded-3xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-500`}>
                        <step.icon className="w-12 h-12 text-white" />
                      </div>
                      {/* Step number */}
                      <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-[#75055B] border-4 border-white flex items-center justify-center text-sm font-bold text-white shadow-lg group-hover:bg-[#DB4789] transition-colors duration-300">
                        {index + 1}
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold mb-4 group-hover:text-[#75055B] transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Connection line for desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-full w-12 h-1 bg-gradient-to-r from-[#DB4789]/30 to-transparent -translate-x-6" />
                )}
              </div>
            ))}
          </div>

          {/* After Booking Section */}
          <div className="bg-gradient-to-r from-[#75055B] to-[#DB4789] rounded-3xl p-8 md:p-12 shadow-2xl text-white">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-2xl font-bold mb-3">After Booking</h4>
                <p className="text-white/90 text-lg leading-relaxed">
                  Get instant confirmation, secure payment processing, and 24/7 customer support. Track your bookings
                  and leave reviews to help the community.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
